// ============================================================================
// Central i18n catalog for the SHIPPED app (`app/`).
//
// This is the single source of truth for translated UI strings. It replaces the
// scattered inline `isAr ? "..." : "..."` ternary pattern. The legacy root `src/`
// `LanguageContext` / `locales/*` system is DEPRECATED and not used here.
//
// Parity is enforced two ways:
//   1. `ar` is typed as `Record<TranslationKey, string>`, so TypeScript fails the
//      build if any key present in `en` is missing from `ar`.
//   2. `i18n.parity.test.ts` asserts the key sets are identical (catches *extra*
//      keys in `ar` and empty strings that the type system allows).
//
// Usage:
//   const t = useT();
//   <span>{t("nav.cockpit")}</span>
//   <span>{t("common.foundCount", { count: 3 })}</span>
// ============================================================================
import { useMemo } from "react";
import { useAppContext, LanguageType } from "./mockState";

export type Locale = LanguageType;

// --- English source dictionary (authoritative key set) ----------------------
export const en = {
  // Brand / shell
  "brand.name": "i-AM Vault",
  "brand.subtitle": "Matter-First Legal OS",
  "shell.signOut": "Sign out",

  // Sidebar sections
  "nav.section.core": "Core",
  "nav.section.people": "People & Portfolio",
  "nav.section.create": "Authoring Studio",
  "nav.section.intelligence": "Legal Intelligence",
  "nav.section.operations": "Firm Operations",
  "nav.section.analyze": "Business Intelligence",

  // Sidebar items
  "nav.cockpit": "Firm Portfolio",
  "nav.myDay": "My Day",
  "nav.calendar": "Calendar",
  "nav.alerts": "Alert stream",
  "nav.clients": "Clients",
  "nav.cases": "Cases Workspace",
  "nav.contracts": "Contracts Workspace",
  "nav.parties": "Authorized Parties",
  "nav.drafting": "Drafting Studio",
  "nav.agentWorkspace": "Agent Workspace",
  "nav.clauseLibrary": "Clause Library",
  "nav.templates": "Document Templates",
  "nav.knowledgeBase": "Knowledge Base",
  "nav.tasks": "Actionable Tasks",
  "nav.workQueue": "Work Queue",
  "nav.timeTracking": "Time Tracking",
  "nav.exports": "Exports Library",
  "nav.billing": "Billing Ledger",
  "nav.analytics": "Firm Analytics",
  "nav.search": "Faceted Search",
  "nav.communications": "Client Comms Feed",
  "nav.documents": "Document Storage",
  "nav.trash": "Trash Recovery",
  "nav.portalOps": "Client Portal",
  "nav.controlTower": "Control Tower",
  "nav.settings": "Firm Settings",

  // Common actions / states
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.confirm": "Confirm",
  "common.delete": "Delete",
  "common.edit": "Edit",
  "common.create": "Create",
  "common.back": "Back",
  "common.next": "Next",
  "common.continue": "Continue",
  "common.search": "Search",
  "common.loading": "Loading…",
  "common.saving": "Saving…",
  "common.noResults": "No matching results found.",
  "common.foundCount": "{count} found",
  "common.required": "Required",
  "common.optional": "Optional",
  "common.yes": "Yes",
  "common.no": "No",
  "common.retry": "Retry",
  "common.email": "Email",
  "common.name": "Name",
  "common.submit": "Submit",

  // Global search
  "search.placeholder": "Search files, leads, contracts… (⌘K)",
  "search.instantMatches": "Instant Search Matches",
  "search.noMatches": "No matching results found.",

  // AI Counsel panel — modes
  "ai.mode.firm": "Firm Mode",
  "ai.mode.case": "Matter Mode",
  "ai.mode.contract": "Matter Mode",
  "ai.mode.client": "Client Mode",
  "ai.mode.drafting": "Drafting Studio",
  "ai.mode.pipeline": "CRM Mode",
  "ai.mode.billing": "Billing Advisory Mode",
  "ai.connectedTo": "Connected to VAULT-CO-PILOT",
  "ai.thinking": "Analyzing…",
  "ai.inputPlaceholder": "Ask AI Counsel…",
  "ai.error.generic": "The AI could not respond right now. Please try again.",
  "ai.taskCreated": "Successfully created matter task!",
  "ai.sentToDrafting": "Sent to Drafting Studio",

  // Onboarding / firm bootstrap
  "onboarding.badge": "Phase 12 · Onboarding",
  "onboarding.title": "Set up your firm workspace",
  "onboarding.subtitle": "Your account is signed in, but it isn't yet linked to a firm. Create a new firm to start, or redeem an invitation token from your firm administrator.",
  "onboarding.createCard.title": "Create a new firm",
  "onboarding.createCard.desc": "You'll become the firm OWNER and can invite teammates afterwards.",
  "onboarding.inviteCard.title": "Accept an invite",
  "onboarding.inviteCard.desc": "Use the invite token shared with you by your firm administrator.",
  "onboarding.firmName": "Firm name",
  "onboarding.jurisdiction": "Jurisdiction",
  "onboarding.creating": "Creating…",
  "onboarding.createFirm": "Create firm",
  "onboarding.inviteToken": "Invite token",
  "onboarding.inviteTokenPlaceholder": "Paste the invite token here",
  "onboarding.redeeming": "Redeeming…",
  "onboarding.redeemInvite": "Redeem invite",
  "onboarding.firmCreated": "Firm created. Refreshing your session…",
  "onboarding.firmDeclined": "Firm creation declined by the server.",
  "onboarding.firmFailed": "Firm creation failed.",
  "onboarding.inviteAccepted": "Invite accepted. Refreshing your session…",
  "onboarding.inviteFailed": "Invite could not be redeemed. Check the token and try again.",
  "onboarding.inviteRedemptionFailed": "Invite redemption failed.",

  // App shell surfaces (alias/fallback pages in App.tsx)
  "shell.loadingWorkspace": "Loading workspace…",
  "shell.knowledge.title": "Knowledge Base",
  "shell.knowledge.desc": "Knowledge precedents, candidate review, and firm knowledge search are available from this workspace entry.",
  "shell.knowledge.articles": "Knowledge articles",
  "shell.knowledge.articlesVal": "Live",
  "shell.knowledge.articlesDetail": "Court precedents, laws, and internal knowledge.",
  "shell.knowledge.candidate": "Candidate review",
  "shell.knowledge.candidateVal": "Ready",
  "shell.knowledge.candidateDetail": "Approve or reject pending knowledge candidates.",
  "shell.knowledge.scope": "Search scope",
  "shell.knowledge.scopeVal": "Firm",
  "shell.knowledge.scopeDetail": "Knowledge search remains restricted to the firm workspace.",
  "shell.team.title": "Team Management",
  "shell.team.desc": "Team member roster, role assignments, and workload visibility for firm operators.",
  "shell.team.members": "Team members",
  "shell.team.membersVal": "Live",
  "shell.team.membersDetail": "Member list and active status overview.",
  "shell.team.roleMatrix": "Role matrix",
  "shell.team.roleMatrixVal": "Owner / Admin / Member",
  "shell.team.roleMatrixDetail": "Role access is managed from the firm workspace.",
  "shell.team.workload": "Workload review",
  "shell.team.workloadVal": "Balanced",
  "shell.team.workloadDetail": "Workload and utilisation checks remain available to firm leadership.",
  "shell.owner.title": "Firm Operations",
  "shell.owner.desc": "Operational oversight across revenue, utilisation, client health, and referral performance.",
  "shell.owner.revenue": "Revenue snapshot",
  "shell.owner.revenueVal": "Tracked",
  "shell.owner.revenueDetail": "Revenue review remains visible from firm operations.",
  "shell.owner.utilisation": "Team utilisation",
  "shell.owner.utilisationVal": "Monitored",
  "shell.owner.utilisationDetail": "Utilisation and workload posture are available for owner review.",
  "shell.owner.portfolio": "Client portfolio",
  "shell.owner.portfolioVal": "Active",
  "shell.owner.portfolioDetail": "Portfolio health and referral controls stay connected to firm operations.",
  "shell.productionPending.desc": "This production surface is waiting for its live data binding. No seeded preview records are rendered outside approved mock mode.",
  "shell.billingRecovery.title": "Firm access requires billing recovery",
  "shell.billingRecovery.restore": "Owners and billing users can restore access from Firm Settings.",

  // Client portal (public, client-facing)
  "portal.loading": "Loading secure client portal…",
  "portal.access.badge": "Secure Portal Access",
  "portal.session.expiredTitle": "Session expired",
  "portal.unavailableTitle": "Portal unavailable",
  "portal.session.expiredBody": "Your secure session expired after 60 minutes of inactivity. Reopen the portal link to continue.",
  "portal.brand": "i-AM Vault Client Portal",
  "portal.onboarding.title": "New client onboarding",
  "portal.access.title": "Secure matter access",
  "portal.onboarding.subtitle": "Complete the guided intake wizard, upload context, and submit for firm review.",
  "portal.access.subtitle": "Review matter status, visible documents, invoice posture, and signatory actions in one secure workspace.",
  "portal.role": "Role",
  "portal.timeout": "Timeout",
  "portal.timeout.value": "60 min",
  "portal.contact": "Contact",
  "portal.email": "Email",
  "portal.emailFallback": "Secure link",
  "portal.contactFallback": "Client",
  "portal.inactivityWarning": "Session inactivity warning: {count} minute(s) remaining before secure logout.",
  "portal.sessionIssued": "Session window issued until {time}",
  "portal.wizard.title": "7-step wizard",
  "portal.step.consent": "Consent",
  "portal.step.clientType": "Client Type",
  "portal.step.identity": "Identity",
  "portal.step.corporateKyc": "Corporate KYC & UBO",
  "portal.step.risk": "Risk Assessment",
  "portal.step.uploads": "Uploads",
  "portal.step.matterContext": "Matter Context",
  "portal.fullName": "Full name",
  "portal.clientType": "Client type",
  "portal.entity": "Entity",
  "portal.individual": "Individual",
  "portal.urgency": "Urgency",
  "portal.urgency.standard": "Standard",
  "portal.urgency.expedited": "Expedited",
  "portal.urgency.urgent": "Urgent",
  "portal.companyName": "Company name",
  "portal.registrationNumber": "Registration number",
  "portal.uboOwnership": "UBO ownership %",
  "portal.pep": "PEP declared",
  "portal.sanctions": "Sanctions concern",
  "portal.complexOwnership": "Complex ownership",
  "portal.sourceOfFunds": "Source of funds",
  "portal.uploadSummary": "Upload summary",
  "portal.matterContext": "Matter context",
  "portal.confirmConsent": "Confirm consent",
  "portal.submitOnboarding": "Submit onboarding",
  "portal.submittedThanks": "Thank you. Your intake has been submitted for firm review. You will receive structured feedback if additional information is required.",
  "portal.clientStatus": "Client status",
  "portal.kyc": "KYC",
  "portal.visibleMatters": "Visible matters",
  "portal.matterScoped": "Matter-scoped access by assigned role.",
  "portal.sharedDocs": "Shared documents",
  "portal.portalSafeDocs": "Portal-safe document visibility only.",
  "portal.outstandingBilling": "Outstanding billing",
  "portal.invoicesPerPermissions": "Invoices shown per contact permissions.",
  "portal.workspaceTitle": "Matter and document workspace",
  "portal.launchEsign": "Launch e-sign",
  "portal.noImmediateEvent": "No immediate event",
  "portal.noDate": "No date",
  "portal.noMattersVisible": "No matters are visible for this token.",
  "portal.documents": "Documents",
  "portal.billingPosture": "Billing and signature posture",
  "portal.esignNote": "E-sign UX is enabled through the secure portal action rail for signatory-scoped access.",
  "portal.pendingShort": "pending",
  "portal.submittingPackage": "Submitting onboarding package…",
  "portal.submittedForReview": "Submitted for review. Risk level: {risk}.",
  "portal.unableToOpen": "Unable to open the client portal.",
  "portal.unableToSubmit": "Unable to submit onboarding.",

  // Admin control tower
  "admin.restricted.title": "Control Tower restricted",
  "admin.restricted.body": "This route is reserved for platform owner and super-admin claims. No tenant operations are exposed to firm users.",
  "admin.systemAdmin": "System Admin",
  "admin.controlTower": "Control Tower",
  "admin.controlTower.desc": "Read-only production operations console for tenant posture, subscription risk, and global audit activity. Mutating controls remain deferred until role-matrix tests and operator approval are complete.",
  "admin.firms": "Firms",
  "admin.active": "Active",
  "admin.suspended": "Suspended",
  "admin.billingRisk": "Billing risk",
  "admin.tenantRegister": "Tenant register",
  "admin.col.firm": "Firm",
  "admin.col.status": "Status",
  "admin.col.subscription": "Subscription",
  "admin.col.members": "Members",
  "admin.col.created": "Created",
  "admin.auditStream": "Global audit stream",

  // Owner center referrals
  "owner.referrals.title": "Referral Operations",
  "owner.referrals.desc": "Referral health, conversion readiness, and growth oversight.",
  "owner.referrals.activeSources": "Active referral sources",
  "owner.referrals.pipelineConversion": "Conversion readiness",
  "owner.referrals.reviewQueue": "Operational control",
  "owner.referrals.ready": "Ready",
  "owner.referrals.clear": "Clear",

  // Trash recovery
  "trash.retention": "Retention",
  "trash.title": "Trash Recovery",
  "trash.desc": "Recoverable deleted records, retention windows, purge controls, and legal-hold blocking for firm-scoped data.",
  "trash.ownerAdmin": "OWNER/ADMIN",
  "trash.readOnly": "READ ONLY",
  "trash.recoverable": "Recoverable records",
  "trash.itemCount": "{count} item(s)",
  "trash.empty": "No recoverable records are currently in firm trash.",
  "trash.restore": "Restore",
  "trash.purge": "Purge",
  "trash.legalHold": "LEGAL HOLD",
  "trash.recordMeta": "deleted {date} by {who} / retention {until}",
  "trash.onlyOwnersManage": "Only owners and admins can manage trash recovery.",
  "trash.restoring": "Restoring record…",
  "trash.purging": "Purging record…",
  "trash.restoredTo": "Record restored to {path}.",
  "trash.purged": "Trash record purged.",
  "trash.actionFailed": "Trash action failed.",
  "trash.permissionRequired": "Only owners and admins can perform this action.",
  "trash.legalHoldBlocked": "This record is under legal hold and cannot be purged.",

  // --- Field validation & auth (T6.3) ---------------------------------------
  "validation.required": "This field is required.",
  "validation.requiredNamed": "{field} is required.",
  "validation.invalidEmail": "Enter a valid email address.",
  "validation.tooShort": "Must be at least {min} characters.",
  "validation.mismatch": "The values do not match.",
  "validation.emailAndPasswordRequired": "Email and password are required.",
  "auth.invalidCredentials": "Invalid email or password.",
  "auth.signInFailed": "Sign-in failed.",
  "auth.googleFailed": "Google sign-in failed.",
  "auth.microsoftFailed": "Microsoft sign-in failed.",
} as const;

export type TranslationKey = keyof typeof en;

// --- Arabic dictionary (must cover every key in `en`) -----------------------
// Typed as Record<TranslationKey, string> so a missing key fails `tsc`.
export const ar: Record<TranslationKey, string> = {
  // Brand / shell
  "brand.name": "آي-أم فولت",
  "brand.subtitle": "نظام التشغيل القانوني المرتكز على الملفات",
  "shell.signOut": "تسجيل الخروج",

  // Sidebar sections
  "nav.section.core": "الأساسيات",
  "nav.section.people": "العلاقات والملفات",
  "nav.section.create": "استوديو التحرير",
  "nav.section.intelligence": "الذكاء القانوني",
  "nav.section.operations": "العمليات التنفيذية",
  "nav.section.analyze": "ذكاء الأعمال",

  // Sidebar items
  "nav.cockpit": "لوحة قيادة المكتب",
  "nav.myDay": "يومي",
  "nav.calendar": "التقويم",
  "nav.alerts": "سجل التنبيهات",
  "nav.clients": "العملاء",
  "nav.cases": "مساحة القضايا",
  "nav.contracts": "مساحة العقود",
  "nav.parties": "الأطراف المخوَّلة",
  "nav.drafting": "استوديو الصياغة",
  "nav.agentWorkspace": "مساحة عمل العميل الذكي",
  "nav.clauseLibrary": "مكتبة البنود",
  "nav.templates": "النماذج المستندية",
  "nav.knowledgeBase": "قاعدة المعرفة",
  "nav.tasks": "المهام القابلة للتنفيذ",
  "nav.workQueue": "طابور العمل",
  "nav.timeTracking": "تتبّع الوقت",
  "nav.exports": "مكتبة التصدير",
  "nav.billing": "سجل الفواتير",
  "nav.analytics": "تحليلات المكتب",
  "nav.search": "البحث المُصنَّف",
  "nav.communications": "موجز اتصالات العملاء",
  "nav.documents": "تخزين المستندات",
  "nav.trash": "استرداد المحذوفات",
  "nav.portalOps": "بوابة العملاء",
  "nav.controlTower": "مركز التحكم",
  "nav.settings": "إعدادات المكتب",

  // Common actions / states
  "common.save": "حفظ",
  "common.cancel": "إلغاء",
  "common.close": "إغلاق",
  "common.confirm": "تأكيد",
  "common.delete": "حذف",
  "common.edit": "تعديل",
  "common.create": "إنشاء",
  "common.back": "رجوع",
  "common.next": "التالي",
  "common.continue": "متابعة",
  "common.search": "بحث",
  "common.loading": "جارٍ التحميل…",
  "common.saving": "جارٍ الحفظ…",
  "common.noResults": "لا توجد نتائج مطابقة.",
  "common.foundCount": "{count} نتيجة",
  "common.required": "مطلوب",
  "common.optional": "اختياري",
  "common.yes": "نعم",
  "common.no": "لا",
  "common.retry": "إعادة المحاولة",
  "common.email": "البريد الإلكتروني",
  "common.name": "الاسم",
  "common.submit": "إرسال",

  // Global search
  "search.placeholder": "ابحث في الملفات والعملاء المحتملين والعقود… (⌘K)",
  "search.instantMatches": "نتائج البحث الفورية",
  "search.noMatches": "لا توجد نتائج مطابقة.",

  // AI Counsel panel — modes
  "ai.mode.firm": "نمط المكتب",
  "ai.mode.case": "نمط القضية",
  "ai.mode.contract": "نمط العقد",
  "ai.mode.client": "نمط محفظة العميل",
  "ai.mode.drafting": "نمط التحرير المستقل",
  "ai.mode.pipeline": "نمط إدارة تدفق الصفقات",
  "ai.mode.billing": "النمط الاستشاري المالي",
  "ai.connectedTo": "متصل بـ VAULT-CO-PILOT",
  "ai.thinking": "جارٍ التحليل…",
  "ai.inputPlaceholder": "اسأل المستشار الذكي…",
  "ai.error.generic": "تعذّر على المساعد الذكي الرد الآن. يرجى المحاولة مرة أخرى.",
  "ai.taskCreated": "تم إنشاء مهمة الملف بنجاح!",
  "ai.sentToDrafting": "أُرسل إلى استوديو الصياغة",

  // Onboarding / firm bootstrap
  "onboarding.badge": "المرحلة 12 · الإعداد الأولي",
  "onboarding.title": "إعداد مساحة عمل مكتبك",
  "onboarding.subtitle": "تم تسجيل دخول حسابك، لكنه غير مرتبط بعد بأي مكتب. أنشئ مكتباً جديداً للبدء، أو استخدم رمز دعوة من مدير مكتبك.",
  "onboarding.createCard.title": "إنشاء مكتب جديد",
  "onboarding.createCard.desc": "ستصبح مالك المكتب، ويمكنك دعوة زملائك لاحقاً.",
  "onboarding.inviteCard.title": "قبول دعوة",
  "onboarding.inviteCard.desc": "استخدم رمز الدعوة الذي شاركه معك مدير مكتبك.",
  "onboarding.firmName": "اسم المكتب",
  "onboarding.jurisdiction": "الاختصاص القضائي",
  "onboarding.creating": "جارٍ الإنشاء…",
  "onboarding.createFirm": "إنشاء المكتب",
  "onboarding.inviteToken": "رمز الدعوة",
  "onboarding.inviteTokenPlaceholder": "الصق رمز الدعوة هنا",
  "onboarding.redeeming": "جارٍ التحقق…",
  "onboarding.redeemInvite": "تأكيد الدعوة",
  "onboarding.firmCreated": "تم إنشاء المكتب. جارٍ تحديث جلستك…",
  "onboarding.firmDeclined": "رفض الخادم إنشاء المكتب.",
  "onboarding.firmFailed": "فشل إنشاء المكتب.",
  "onboarding.inviteAccepted": "تم قبول الدعوة. جارٍ تحديث جلستك…",
  "onboarding.inviteFailed": "تعذّر تأكيد الدعوة. تحقق من الرمز وحاول مرة أخرى.",
  "onboarding.inviteRedemptionFailed": "فشل تأكيد الدعوة.",

  // App shell surfaces (alias/fallback pages in App.tsx)
  "shell.loadingWorkspace": "جارٍ تحميل مساحة العمل…",
  "shell.knowledge.title": "قاعدة المعرفة",
  "shell.knowledge.desc": "السوابق المعرفية ومراجعة المرشحات والبحث في معرفة المكتب متاحة من مدخل مساحة العمل هذه.",
  "shell.knowledge.articles": "مقالات المعرفة",
  "shell.knowledge.articlesVal": "نشط",
  "shell.knowledge.articlesDetail": "السوابق القضائية والقوانين والمعرفة الداخلية.",
  "shell.knowledge.candidate": "مراجعة المرشحات",
  "shell.knowledge.candidateVal": "جاهز",
  "shell.knowledge.candidateDetail": "اعتماد أو رفض مرشحات المعرفة المعلّقة.",
  "shell.knowledge.scope": "نطاق البحث",
  "shell.knowledge.scopeVal": "المكتب",
  "shell.knowledge.scopeDetail": "يظل البحث في المعرفة مقصوراً على مساحة عمل المكتب.",
  "shell.team.title": "إدارة الفريق",
  "shell.team.desc": "قائمة أعضاء الفريق وإسناد الأدوار ووضوح أعباء العمل لمشغّلي المكتب.",
  "shell.team.members": "أعضاء الفريق",
  "shell.team.membersVal": "نشط",
  "shell.team.membersDetail": "قائمة الأعضاء ونظرة عامة على الحالة النشطة.",
  "shell.team.roleMatrix": "مصفوفة الأدوار",
  "shell.team.roleMatrixVal": "مالك / مدير / عضو",
  "shell.team.roleMatrixDetail": "تُدار صلاحيات الأدوار من مساحة عمل المكتب.",
  "shell.team.workload": "مراجعة أعباء العمل",
  "shell.team.workloadVal": "متوازن",
  "shell.team.workloadDetail": "تظل فحوص أعباء العمل والاستغلال متاحة لقيادة المكتب.",
  "shell.owner.title": "مركز المالك",
  "shell.owner.desc": "لمحة عن الإيرادات واستغلال الفريق وصحة محفظة العملاء والإشراف على الإحالات.",
  "shell.owner.revenue": "لمحة الإيرادات",
  "shell.owner.revenueVal": "متتبَّع",
  "shell.owner.revenueDetail": "تظل مراجعة الإيرادات مرئية من مركز المالك.",
  "shell.owner.utilisation": "استغلال الفريق",
  "shell.owner.utilisationVal": "مُراقَب",
  "shell.owner.utilisationDetail": "وضع الاستغلال وأعباء العمل متاح لمراجعة المالك.",
  "shell.owner.portfolio": "صحة محفظة العملاء",
  "shell.owner.portfolioVal": "نشطة",
  "shell.owner.portfolioDetail": "تبقى صحة المحفظة وضوابط الإحالة متصلة بسير عمل المالك.",
  "shell.productionPending.desc": "هذا السطح الإنتاجي بانتظار ربطه بالبيانات الحية. لا تُعرض أي سجلات معاينة خارج وضع المحاكاة المعتمد.",
  "shell.billingRecovery.title": "يتطلب الوصول إلى المكتب استرداد الفوترة",
  "shell.billingRecovery.restore": "يمكن للمالكين ومستخدمي الفوترة استعادة الوصول من إعدادات المكتب.",

  // Client portal (public, client-facing)
  "portal.loading": "جارٍ تحميل بوابة العميل الآمنة…",
  "portal.access.badge": "وصول البوابة الآمن",
  "portal.session.expiredTitle": "انتهت الجلسة",
  "portal.unavailableTitle": "البوابة غير متاحة",
  "portal.session.expiredBody": "انتهت جلستك الآمنة بعد 60 دقيقة من عدم النشاط. أعد فتح رابط البوابة للمتابعة.",
  "portal.brand": "بوابة عملاء آي-أم فولت",
  "portal.onboarding.title": "تسجيل عميل جديد",
  "portal.access.title": "وصول آمن إلى الملف",
  "portal.onboarding.subtitle": "أكمل معالج الإدخال الموجَّه، وارفع السياق، وأرسله لمراجعة المكتب.",
  "portal.access.subtitle": "راجع حالة الملف والمستندات المرئية ووضع الفواتير وإجراءات التوقيع في مساحة عمل آمنة واحدة.",
  "portal.role": "الدور",
  "portal.timeout": "المهلة",
  "portal.timeout.value": "60 دقيقة",
  "portal.contact": "جهة الاتصال",
  "portal.email": "البريد الإلكتروني",
  "portal.emailFallback": "رابط آمن",
  "portal.contactFallback": "العميل",
  "portal.inactivityWarning": "تحذير عدم النشاط: تبقّى {count} دقيقة قبل تسجيل الخروج الآمن.",
  "portal.sessionIssued": "نافذة الجلسة سارية حتى {time}",
  "portal.wizard.title": "معالج من 7 خطوات",
  "portal.step.consent": "الموافقة",
  "portal.step.clientType": "نوع العميل",
  "portal.step.identity": "الهوية",
  "portal.step.corporateKyc": "اعرف عميلك للشركات والمستفيد الحقيقي",
  "portal.step.risk": "تقييم المخاطر",
  "portal.step.uploads": "المرفقات",
  "portal.step.matterContext": "سياق الملف",
  "portal.fullName": "الاسم الكامل",
  "portal.clientType": "نوع العميل",
  "portal.entity": "شركة",
  "portal.individual": "فرد",
  "portal.urgency": "الأولوية",
  "portal.urgency.standard": "عادية",
  "portal.urgency.expedited": "معجَّلة",
  "portal.urgency.urgent": "عاجلة",
  "portal.companyName": "اسم الشركة",
  "portal.registrationNumber": "رقم السجل",
  "portal.uboOwnership": "نسبة ملكية المستفيد الحقيقي %",
  "portal.pep": "شخص سياسي ممثَّل",
  "portal.sanctions": "مخاوف عقوبات",
  "portal.complexOwnership": "ملكية معقّدة",
  "portal.sourceOfFunds": "مصدر الأموال",
  "portal.uploadSummary": "ملخص المرفقات",
  "portal.matterContext": "سياق الملف",
  "portal.confirmConsent": "تأكيد الموافقة",
  "portal.submitOnboarding": "إرسال التسجيل",
  "portal.submittedThanks": "شكراً لك. تم إرسال إدخالك لمراجعة المكتب. ستتلقى ملاحظات منظَّمة إذا لزمت معلومات إضافية.",
  "portal.clientStatus": "حالة العميل",
  "portal.kyc": "اعرف عميلك",
  "portal.visibleMatters": "الملفات المرئية",
  "portal.matterScoped": "وصول محدد بالملف وفق الدور المُسنَد.",
  "portal.sharedDocs": "المستندات المشتركة",
  "portal.portalSafeDocs": "رؤية مستندات آمنة عبر البوابة فقط.",
  "portal.outstandingBilling": "الفواتير المستحقة",
  "portal.invoicesPerPermissions": "تُعرض الفواتير وفق صلاحيات جهة الاتصال.",
  "portal.workspaceTitle": "مساحة عمل الملفات والمستندات",
  "portal.launchEsign": "بدء التوقيع الإلكتروني",
  "portal.noImmediateEvent": "لا يوجد حدث وشيك",
  "portal.noDate": "لا يوجد تاريخ",
  "portal.noMattersVisible": "لا توجد ملفات مرئية لهذا الرمز.",
  "portal.documents": "المستندات",
  "portal.billingPosture": "وضع الفوترة والتوقيع",
  "portal.esignNote": "تجربة التوقيع الإلكتروني مفعَّلة عبر شريط إجراءات البوابة الآمن للوصول المحدد للموقِّعين.",
  "portal.pendingShort": "قيد الانتظار",
  "portal.submittingPackage": "جارٍ إرسال حزمة التسجيل…",
  "portal.submittedForReview": "تم الإرسال للمراجعة. مستوى المخاطر: {risk}.",
  "portal.unableToOpen": "تعذّر فتح بوابة العميل.",
  "portal.unableToSubmit": "تعذّر إرسال التسجيل.",

  // Admin control tower
  "admin.restricted.title": "برج التحكم مقيَّد",
  "admin.restricted.body": "هذا المسار محجوز لمالك المنصة وصلاحيات المشرف الأعلى. لا تُتاح أي عمليات للمستأجرين لمستخدمي المكاتب.",
  "admin.systemAdmin": "مشرف النظام",
  "admin.controlTower": "برج التحكم",
  "admin.controlTower.desc": "وحدة تحكم عمليات إنتاجية للقراءة فقط لوضع المستأجرين ومخاطر الاشتراك ونشاط التدقيق الشامل. تظل عناصر التحكم التعديلية مؤجَّلة حتى اكتمال اختبارات مصفوفة الأدوار وموافقة المشغّل.",
  "admin.firms": "المكاتب",
  "admin.active": "نشِط",
  "admin.suspended": "موقوف",
  "admin.billingRisk": "مخاطر الفوترة",
  "admin.tenantRegister": "سجل المستأجرين",
  "admin.col.firm": "المكتب",
  "admin.col.status": "الحالة",
  "admin.col.subscription": "الاشتراك",
  "admin.col.members": "الأعضاء",
  "admin.col.created": "تاريخ الإنشاء",
  "admin.auditStream": "تدفق التدقيق الشامل",

  // Owner center referrals
  "owner.referrals.title": "إحالات مركز المالك",
  "owner.referrals.desc": "صحة الإحالات وعائد التحويل وعناصر تحكم النمو على مستوى المالك.",
  "owner.referrals.activeSources": "مصادر الإحالة النشطة",
  "owner.referrals.pipelineConversion": "تحويل خط الأعمال",
  "owner.referrals.reviewQueue": "قائمة مراجعة المالك",
  "owner.referrals.ready": "جاهز",
  "owner.referrals.clear": "خالٍ",

  // Trash recovery
  "trash.retention": "الاحتفاظ",
  "trash.title": "استرداد المحذوفات",
  "trash.desc": "السجلات المحذوفة القابلة للاسترداد ونوافذ الاحتفاظ وعناصر تحكم الإزالة وحجب الحجز القانوني للبيانات الخاصة بالمكتب.",
  "trash.ownerAdmin": "مالك/مشرف",
  "trash.readOnly": "قراءة فقط",
  "trash.recoverable": "السجلات القابلة للاسترداد",
  "trash.itemCount": "{count} عنصر",
  "trash.empty": "لا توجد حالياً سجلات قابلة للاسترداد في سلة المكتب.",
  "trash.restore": "استعادة",
  "trash.purge": "إزالة نهائية",
  "trash.legalHold": "حجز قانوني",
  "trash.recordMeta": "حُذف {date} بواسطة {who} / الاحتفاظ {until}",
  "trash.onlyOwnersManage": "يمكن للمالكين والمشرفين فقط إدارة استرداد المحذوفات.",
  "trash.restoring": "جارٍ استعادة السجل…",
  "trash.purging": "جارٍ إزالة السجل…",
  "trash.restoredTo": "تمت استعادة السجل إلى {path}.",
  "trash.purged": "تمت إزالة سجل السلة نهائياً.",
  "trash.actionFailed": "فشل إجراء السلة.",
  "trash.permissionRequired": "يمكن للمالكين والمشرفين فقط تنفيذ هذا الإجراء.",
  "trash.legalHoldBlocked": "هذا السجل تحت حجز قانوني ولا يمكن إزالته.",

  // --- Field validation & auth (T6.3) ---------------------------------------
  "validation.required": "هذا الحقل مطلوب.",
  "validation.requiredNamed": "حقل {field} مطلوب.",
  "validation.invalidEmail": "أدخل بريداً إلكترونياً صالحاً.",
  "validation.tooShort": "يجب ألا يقل عن {min} أحرف.",
  "validation.mismatch": "القيم غير متطابقة.",
  "validation.emailAndPasswordRequired": "البريد الإلكتروني وكلمة المرور مطلوبان.",
  "auth.invalidCredentials": "البريد الإلكتروني أو كلمة المرور غير صحيحة.",
  "auth.signInFailed": "فشل تسجيل الدخول.",
  "auth.googleFailed": "فشل تسجيل الدخول عبر Google.",
  "auth.microsoftFailed": "فشل تسجيل الدخول عبر Microsoft.",
};

export const dictionaries: Record<Locale, Record<TranslationKey, string>> = { en, ar };

/** Pure translate function — usable outside React (e.g. tests, utilities). */
export function translate(
  lang: Locale,
  key: TranslationKey,
  params?: Record<string, string | number>,
): string {
  const template = dictionaries[lang]?.[key] ?? en[key] ?? String(key);
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_match, name: string) =>
    name in params ? String(params[name]) : `{${name}}`,
  );
}

export type TFunction = (
  key: TranslationKey,
  params?: Record<string, string | number>,
) => string;

/** React hook returning a memoized `t(key, params?)` bound to the active language. */
export function useT(): TFunction {
  const { lang } = useAppContext();
  return useMemo<TFunction>(() => (key, params) => translate(lang, key, params), [lang]);
}

// ============================================================================
// Parity verification (L1.3)
//
// TypeScript already enforces *key* parity at build time because `ar` is typed
// `Record<TranslationKey, string>` — a missing key fails `tsc --noEmit` and an
// extra key fails excess-property checking. This runtime verifier additionally
// catches values the type system cannot: empty / whitespace-only strings and any
// drift introduced via non-literal mutation. Call it from a dev guard or CI.
// ============================================================================
export interface I18nParityProblem {
  locale: Locale;
  key: string;
  issue: "missing" | "empty";
}

export function verifyI18nParity(): I18nParityProblem[] {
  const problems: I18nParityProblem[] = [];
  const enKeys = Object.keys(en) as TranslationKey[];

  (Object.keys(dictionaries) as Locale[]).forEach((locale) => {
    const dict = dictionaries[locale] as Record<string, string>;
    // Missing keys (relative to the authoritative English set).
    enKeys.forEach((key) => {
      if (!(key in dict)) {
        problems.push({ locale, key, issue: "missing" });
      } else if (!String(dict[key]).trim()) {
        problems.push({ locale, key, issue: "empty" });
      }
    });
    // Extra keys not present in English.
    Object.keys(dict).forEach((key) => {
      if (!(key in en)) {
        problems.push({ locale, key, issue: "missing" });
      }
    });
  });

  return problems;
}

